// Builder controls for option lists and the richer question types.
import { DEFAULT_TOTAL, effectiveScale, isChoice, loopSource, type Definition, type Option, type Question } from '@survey/core';
import { Braces, ListPlus, Pin, PinOff, Plus, X } from 'lucide-react';
import { useI18n } from '../../i18n';
import { Field, Switch } from '../ui';
import { OptionImage } from './MediaEditor';
import { newId } from './model';

type ListKind = 'choice' | 'columns' | 'rows' | 'items' | 'fields';

interface ListProps {
  question: Question;
  kind: ListKind;
  onChange: (q: Question) => void;
  /** Show the per-option points field. */
  scoring?: boolean;
}

/** Editable list of options, matrix columns or rows, sum items or form fields. */
export function OptionList({ question: q, kind, onChange, scoring }: ListProps) {
  const { t } = useI18n();
  const key = kind === 'rows' ? 'rows' : 'options';
  const list = (key === 'rows' ? q.rows : q.options) ?? [];
  const min = kind === 'fields' || kind === 'items' || kind === 'rows' ? 1 : 2;
  const set = (next: Option[]) => onChange({ ...q, [key]: next });
  const patch = (id: string, p: Partial<Option>) => set(list.map((x) => (x.id === id ? { ...x, ...p } : x)));
  const nounKey = { choice: 'qe.option_n', columns: 'qe.column_n', rows: 'qe.row_n', items: 'qe.item_n', fields: 'qe.field_n' }[kind] as 'qe.option_n';
  const addKey = { choice: 'qe.add_option', columns: 'qe.add_column', rows: 'qe.add_row', items: 'qe.add_item', fields: 'qe.add_field' }[kind] as 'qe.add_option';
  const shuffle = kind === 'rows' ? q.randomizeRows : q.randomizeOptions;
  const hasOther = list.some((o) => o.other);
  const add = (extra: Partial<Option> = {}, at = list.length) => {
    const next = list.slice();
    // Keep "Other" last when adding a normal option.
    const otherAt = next.findIndex((o) => o.other);
    const index = !extra.other && otherAt >= 0 && at > otherAt ? otherAt : at;
    next.splice(index, 0, { id: newId(kind === 'rows' ? 'r' : 'o'), label: extra.label ?? t(nounKey, { n: list.length + 1 }), ...extra });
    set(next);
  };
  return (
    <div className="stack gap-8">
      {list.map((o, i) => (
        <div key={o.id} className="row gap-8">
          <span className={`option-bullet ${q.type === 'multiple_choice' || (kind === 'columns' && q.matrixMultiple) ? 'square' : ''} ${kind === 'fields' || kind === 'items' || kind === 'rows' ? 'dash' : ''}`} aria-hidden />
          <input
            className="input"
            value={o.label}
            aria-label={t(nounKey, { n: i + 1 })}
            placeholder={o.other ? t('qe.other_label') : undefined}
            onChange={(e) => patch(o.id, { label: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add({ label: '' }, i + 1);
              }
            }}
          />
          {o.other && <span className="pill">{t('qe.other_badge')}</span>}
          {scoring && (kind === 'choice' || kind === 'columns') && (
            <input
              className="input score-input"
              type="number"
              step="any"
              value={o.score ?? ''}
              placeholder={t('qe.points')}
              aria-label={t('qe.points_for', { label: o.label || t(nounKey, { n: i + 1 }) })}
              onChange={(e) => patch(o.id, { score: e.target.value === '' ? undefined : Number(e.target.value) })}
            />
          )}
          {kind === 'choice' && q.type !== 'dropdown' && (
            <OptionImage url={o.imageUrl} label={o.label || t(nounKey, { n: i + 1 })} onChange={(imageUrl) => patch(o.id, { imageUrl })} />
          )}
          {shuffle && (
            <button
              className={`btn sm icon ${o.fixed ? '' : 'ghost'}`}
              title={o.fixed ? t('qe.pinned') : t('qe.pin')}
              aria-label={o.fixed ? t('qe.unpin_option') : t('qe.pin_option')}
              aria-pressed={!!o.fixed}
              onClick={() => patch(o.id, { fixed: !o.fixed || undefined })}
            >
              {o.fixed ? <Pin /> : <PinOff />}
            </button>
          )}
          <button className="btn ghost sm icon" aria-label={t('qe.remove_option', { n: i + 1 })} disabled={list.length <= min} onClick={() => set(list.filter((x) => x.id !== o.id))}>
            <X />
          </button>
        </div>
      ))}
      <div className="row gap-8 wrap">
        <button className="btn sm" onClick={() => add()}>
          <Plus /> {t(addKey)}
        </button>
        {kind === 'choice' && isChoice(q.type) && !hasOther && (
          <button className="btn sm ghost" onClick={() => add({ label: t('qe.other_label'), other: true, fixed: true })}>
            <ListPlus /> {t('qe.add_other')}
          </button>
        )}
        <span className="grow" />
        {kind !== 'fields' && kind !== 'columns' && (
          <Switch
            checked={!!shuffle}
            onChange={(r) => onChange(kind === 'rows' ? { ...q, randomizeRows: r || undefined } : { ...q, randomizeOptions: r || undefined })}
            label={kind === 'rows' ? t('qe.shuffle_rows') : t('qe.shuffle_options')}
          />
        )}
      </div>
    </div>
  );
}

/** Where a choice question's options come from: written here, or carried from an earlier question. */
export function CarryForwardPicker({ def, question: q, onChange }: { def: Definition; question: Question; onChange: (q: Question) => void }) {
  const { t } = useI18n();
  const idx = def.questions.findIndex((x) => x.id === q.id);
  const myLoop = loopSource(def, q) ? q.sectionTitle?.trim() : undefined;
  const sources = def.questions.slice(0, idx).filter((x) => {
    if (!isChoice(x.type)) return false;
    const theirLoop = loopSource(def, x) ? x.sectionTitle?.trim() : undefined;
    return !theirLoop || theirLoop === myLoop;
  });
  const cf = q.carryForward;
  const value = cf ? `${cf.mode}:${cf.questionId}` : '';
  if (!sources.length && !cf) return null;
  return (
    <Field label={t('qe.options_from')} htmlFor="q-carry" hint={cf ? t('qe.carry_hint') : undefined}>
      <select
        id="q-carry"
        className="select"
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          if (!v) {
            onChange({ ...q, carryForward: undefined, options: q.options?.length ? q.options : [{ id: newId('o'), label: t('qe.option_n', { n: 1 }) }, { id: newId('o'), label: t('qe.option_n', { n: 2 }) }] });
            return;
          }
          const [mode, questionId] = v.split(':') as ['selected' | 'not_selected', string];
          onChange({ ...q, carryForward: { questionId, mode }, options: undefined });
        }}
      >
        <option value="">{t('qe.options_written')}</option>
        {sources.map((s) => {
          const n = def.questions.indexOf(s) + 1;
          const name = `${n}. ${s.questionCode ? `[${s.questionCode}] ` : ''}${s.title || t('common.untitled_question')}`;
          return (
            <optgroup key={s.id} label={name}>
              <option value={`selected:${s.id}`}>{t('qe.carry_selected', { n })}</option>
              <option value={`not_selected:${s.id}`}>{t('qe.carry_not_selected', { n })}</option>
            </optgroup>
          );
        })}
      </select>
    </Field>
  );
}

/** Inserts a {{…}} placeholder for an earlier answer, a hidden field, the loop option or the score. */
export function PipeMenu({ def, question: q, onInsert }: { def: Definition; question: Question; onInsert: (token: string) => void }) {
  const { t } = useI18n();
  const idx = def.questions.findIndex((x) => x.id === q.id);
  const earlier = def.questions.slice(0, idx).filter((x) => x.type !== 'descriptive');
  const fields = def.settings.hiddenFields ?? [];
  const inLoop = !!loopSource(def, q);
  const scored = def.questions.some((x) => x.options?.some((o) => typeof o.score === 'number'));
  if (!earlier.length && !fields.length && !inLoop && !scored) return null;
  return (
    <label className="pipe-menu" title={t('qe.pipe')}>
      <Braces aria-hidden />
      <select
        aria-label={t('qe.pipe')}
        value=""
        onChange={(e) => {
          if (e.target.value) onInsert(`{{${e.target.value}}}`);
        }}
      >
        <option value="">{t('qe.pipe')}</option>
        {inLoop && <option value="loop">{t('qe.pipe_loop')}</option>}
        {scored && <option value="score">{t('qe.pipe_score')}</option>}
        {earlier.length > 0 && (
          <optgroup label={t('qe.pipe_answers')}>
            {earlier.map((x) => (
              <option key={x.id} value={x.questionCode || x.id}>
                {def.questions.indexOf(x) + 1}. {x.title || t('common.untitled_question')}
              </option>
            ))}
          </optgroup>
        )}
        {fields.length > 0 && (
          <optgroup label={t('qe.pipe_fields')}>
            {fields.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </optgroup>
        )}
      </select>
    </label>
  );
}

function num(v: string): number | undefined {
  if (v.trim() === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

export function SliderSettings({ question: q, onChange }: { question: Question; onChange: (q: Question) => void }) {
  const { t } = useI18n();
  const s = effectiveScale(q);
  const set = (p: Partial<typeof s>) => onChange({ ...q, scale: { ...s, ...p } });
  return (
    <div className="grid cols-3">
      <Field label={t('qe.minimum')}>
        <input className="input" type="number" value={s.min} onChange={(e) => set({ min: num(e.target.value) ?? 0 })} />
      </Field>
      <Field label={t('qe.maximum')}>
        <input className="input" type="number" value={s.max} onChange={(e) => set({ max: num(e.target.value) ?? 100 })} />
      </Field>
      <Field label={t('qe.step')}>
        <input className="input" type="number" min={0} step="any" value={s.step ?? 1} onChange={(e) => set({ step: num(e.target.value) })} />
      </Field>
      <ScaleLabels question={q} onChange={onChange} />
    </div>
  );
}

/** Low/high end labels for rating, slider and NPS. */
export function ScaleLabels({ question: q, onChange }: { question: Question; onChange: (q: Question) => void }) {
  const { t } = useI18n();
  const s = effectiveScale(q);
  const base = q.type === 'nps' ? { min: 0, max: 10 } : s;
  return (
    <>
      <Field label={t('qe.low_label')}>
        <input className="input" value={q.scale?.minLabel ?? ''} placeholder={q.type === 'nps' ? t('input.nps_low') : t('qe.low_placeholder')} onChange={(e) => onChange({ ...q, scale: { ...base, ...q.scale, minLabel: e.target.value || undefined } })} />
      </Field>
      <Field label={t('qe.high_label')}>
        <input className="input" value={q.scale?.maxLabel ?? ''} placeholder={q.type === 'nps' ? t('input.nps_high') : t('qe.high_placeholder')} onChange={(e) => onChange({ ...q, scale: { ...base, ...q.scale, maxLabel: e.target.value || undefined } })} />
      </Field>
    </>
  );
}

export function ConstantSumSettings({ question: q, onChange }: { question: Question; onChange: (q: Question) => void }) {
  const { t } = useI18n();
  const v = q.validation ?? {};
  return (
    <div className="grid cols-2">
      <Field label={t('qe.total')} hint={t('qe.total_hint')}>
        <input className="input" type="number" min={1} value={q.total ?? DEFAULT_TOTAL} onChange={(e) => onChange({ ...q, total: num(e.target.value) })} />
      </Field>
      <div className="field" style={{ justifyContent: 'flex-end', paddingBottom: 8 }}>
        <Switch
          checked={!!v.integer}
          onChange={(integer) => {
            const next = { ...v, integer: integer || undefined };
            if (!next.integer) delete next.integer;
            onChange({ ...q, validation: Object.keys(next).length ? next : undefined });
          }}
          label={t('qe.whole_numbers')}
        />
      </div>
    </div>
  );
}
